import type {HasLocalCoordinates} from "./HasLocalCoordinates";
import ShojituerFluegelO3 from "../object3d/ShojituerFluegel";
import Brett from "./Brett";
import Front from "./Front";
import Commons from "oc-commons/Commons";
import Schublade from "./Schublade";
import Bohrlochleiste from "./Bohrlochleiste";
import {v2, v3} from "oc-threejs/short3";
import ConvexUvBufferGeometry from "oc-threejs/geometries/ConvexUvBufferGeometry";
import ThreeBSP from "oc-threejs/ThreeBSP";
import Maths from "oc-commons/Maths";
import SchwebetuerFluegel from "./SchwebetuerFluegel";

export default class ShojituerFluegel implements HasLocalCoordinates {

    tuer: Shojituer;
    object3d: ?THREE.Object3D;
    rahmen: Brett[] = [];
    sprossen: Brett[] = [];
    papier: Brett[] = [];
    griffRahmen: Brett[] = [];
    griffPapier: Brett[] = [];
    // die Box im lokalen System des Parents
    box = new THREE.Box3();
    size = v3();
    griffSchatten: Plane[] = [];

    constructor(tuer) {
        this.tuer = tuer;
        this.sprossenMasse = v3(
            APP.settings.getFloat(`schoji_fluegel_sprossen_breite`, 2),
            APP.settings.getFloat(`schoji_fluegel_sprossen_hoehe`, 2),
            APP.settings.getFloat(`schoji_fluegel_sprossen_tiefe`, 2),
        );
        this.schoji_papier_tiefe = APP.settings.getFloat(`schoji_papier_tiefe`, 0.1);
        this.shoji_sprossen_tuerbreite_anzahl_schritte = APP.settings.getValue(`shoji_sprossen_tuerbreite_anzahl_schritte`, '0:60:90:140:200:1000').split(':').map(x=>parseInt(x,10));
        this.griffMasse = {
            x: APP.settings.getFloat('schoji_griff_rahmen_breite', 2),
            y: APP.settings.getFloat('schoji_griff_rahmen_hoehe', 2),
            s: APP.settings.getFloat('schoji_griff_rahmen_staerke', 2),
            schoji_griff_wunsch_hoehe: APP.settings.getFloat('schoji_griff_wunsch_hoehe', 110),
        };
        this.griffMuldeSize = v3(
            APP.settings.getFloat(`schoji_griffmulde_breite`, 3),
            APP.settings.getFloat(`schoji_griffmulde_hoehe`, 10),
            APP.settings.getFloat(`schoji_griffmulde_tiefe`, 1)
        );
        this.papierSeitenverhaeltnis = APP.settings.getFloat(`schoji_papier_seitenverhaeltnis`, 3/1.80);
        this.papierHoeheMin = APP.settings.getMaxValue(`schoji_papier_hoehe`, 165);
        this.papierHoeheMax = APP.settings.getMaxValue(`schoji_papier_hoehe`, 195);
    }

    get parent() {
        return this.tuer;
    }

    get index() {
        return this.tuer.fluegel.findIndex(f => f === this);
    }

    get schieneIndex() {
        return ((this.schiebetuerLinksVorne ? 1 : 0) + this.index) % 2;
    }

    get element() {
        return this.tuer.schrank.elements[this.index];
    }

    get schrank() {
        return this.tuer.schrank;
    }

    get child3dContainer() {
        return this.object3d;
    }

    get schiebetuerLinksVorne() {
        return this.arrangement.schiebetuerLinksVorne && this.tuer.fluegel.length==2;
    }

    get isShojigriff() {
        return this.arrangement.griffArtikelnummer=='Shojigriff';
    }

    get isOhneGriff() {
        return this.arrangement.griffArtikelnummer=='OhneGriff';
    }

    schrankToLocal(v: THREE.Vector3) {
        return this.tuer.schrankToLocal(v);
    }

    localToSchrank(v: THREE.Vector3) {
        return this.tuer.localToSchrank(v);
    }

    localToSchrankBox(box) {
        return new THREE.Box3(
            this.localToSchrank(box.min),
            this.localToSchrank(box.max)
        );
    }

    fromJSON(json) {
        return this;
    }

    toJSON() {
        return {
            griffHoehe: this.getGriffCenterY(),
            sprossenAufteilung: this.getSprossenAufteilung(),
            size: this.size.toJSON(),
        };
    }

    copy(fluegel,createInterceptor) {
        return this;
    }


    get linksNeedsGriff() {
        if (this.isShojigriff && this.index==1 && this.tuer.fluegel.length==2) {
            return false;
        }
        return this.schiebetuerLinksVorne
            ? (this.index!=1)
            : (this.index!=2);
    }

    get rechtsNeedsGriff() {
        return this.schiebetuerLinksVorne
            ? true
            : (this.index!=0);
    }

    compileRahmenMasse() {
        const griffmulde_rahmen_breite = APP.settings.getFloat(`schoji_fluegel_rahmen_mit_griffmulde_breite`, 8);
        const schoji_fluegel_rahmen_breite = APP.settings.getFloat(`schoji_fluegel_rahmen_breite`, 4);
        const isGriffMulde = !this.isShojigriff;
        this.rahmenMasse = {
            xl: isGriffMulde && this.linksNeedsGriff ? griffmulde_rahmen_breite : schoji_fluegel_rahmen_breite,
            xr: isGriffMulde && this.rechtsNeedsGriff ? griffmulde_rahmen_breite : schoji_fluegel_rahmen_breite,
            y: APP.settings.getFloat(`schoji_fluegel_rahmen_hoehe`, 4),
            z: APP.settings.getFloat(`schoji_fluegel_rahmen_tiefe`, 2),
        };
    }

    isAussenliegend() {
        return APP.arrangement.isShojituerAussen();
    }

    compile() {
        this.compileRahmenMasse();

        APP.model.lazyCreateObject3d(this, () => {
            const o3 = new ShojituerFluegelO3(this);
            this.tuer.child3dContainer.add(o3);
            return o3;
        });

        if (this.isAussenliegend()) {

            this.box = SchwebetuerFluegel.compileFluegelAussenBox(this,{
                brettstaerke: APP.settings.getFloat('schoji_fluegel_rahmen_tiefe'),
                vorsprung_min: APP.settings.getMinValue('schiebetuer_vorsprung', 2.5),
                vorsprung_max: APP.settings.getMaxValue('schiebetuer_vorsprung', 5),
                versatz: APP.settings.getFloat('schiebetuer_versatz', 3),
                ueberstand_unten: APP.settings.getFloat('schiebetuer_ueberstand_unten', 4),
            }).box;

        } else {
            this.box.copy(this.element.box);
            this.box.min.z = this.schrank.elementsBox.max.z - APP.settings.getFloat(`schoji_schiene_${this.schieneIndex+1}_abstand_vorne`, 2.5);
            this.box.max.z = this.box.min.z + this.rahmenMasse.z;
        }

        this.addUeberstandSeitlich();

        this.size = this.box.getSize();

        this.compileAussenrahmen();
        this.compileShojiGriff();
        this.compileSprossenUndPapier();
    }

    addUeberstandSeitlich() {
        let index = this.index;
        let linksNeedsUeberstand = index > 0;
        let rechtsNeedsUeberstand = this.tuer.fluegel.length - 1 - index > 0;
        const ueberstand = APP.settings.getFloat(`schoji_fluegel_ueberstand_seitlich`, 4);
        
        if (linksNeedsUeberstand) {
            this.box.min.x -= ueberstand;
        }
        if (rechtsNeedsUeberstand) {
            this.box.max.x += ueberstand;
        }
    }

    compileAussenrahmen() {
        const lazyRahmen = APP.model.lazyPool(this, 'rahmen', () => new Brett(this, 'Shojituerrahmen'));
        const lazyGriffschatten = APP.model.lazyPool(this, 'griffSchatten', () => new Brett(this, 'Shojituerrahmen'));

        const innerBox = this.box.clone();
        innerBox.min.y += this.rahmenMasse.y;
        innerBox.max.y -= this.rahmenMasse.y;
        innerBox.min.x += this.rahmenMasse.xl;
        innerBox.max.x -= this.rahmenMasse.xr;

        const aussenwand_durchgehend_oben = APP.settings.getBool('aussenwand_durchgehend_oben',false);
        const aussenwand_durchgehend_unten = APP.settings.getBool('aussenwand_durchgehend_unten',false);

        const unten = lazyRahmen.next();
        unten.box.copy(this.box);
        unten.box.max.y = innerBox.min.y;
        unten.box.min.x = (aussenwand_durchgehend_unten ? innerBox : this.box).min.x;
        unten.box.max.x = (aussenwand_durchgehend_unten ? innerBox : this.box).max.x;
        unten.mainAxes = 'xy';

        const oben = lazyRahmen.next();
        oben.box.copy(this.box);
        oben.box.min.y = innerBox.max.y;
        oben.box.min.x = (aussenwand_durchgehend_oben ? innerBox : this.box).min.x;
        oben.box.max.x = (aussenwand_durchgehend_oben ? innerBox : this.box).max.x;
        oben.mainAxes = 'xy';

        const links = lazyRahmen.next();
        links.box.copy(this.box);
        links.box.max.x = innerBox.min.x;
        links.box.min.y = (aussenwand_durchgehend_unten ? this.box : innerBox ).min.y;
        links.box.max.y = (aussenwand_durchgehend_oben ? this.box : innerBox ).max.y;
        links.mainAxes = 'yx';

        const rechts = lazyRahmen.next();
        rechts.box.copy(this.box);
        rechts.box.min.x = innerBox.max.x;
        rechts.box.min.y = (aussenwand_durchgehend_unten ? this.box : innerBox ).min.y;
        rechts.box.max.y = (aussenwand_durchgehend_oben ? this.box : innerBox ).max.y;
        rechts.mainAxes = 'yx';

        this.compileLazyBretter(lazyRahmen);

        // ConcaveGeometry funktioniert nicht gut. Nur wenn es eine wirklich dichte Punktwolke ist. Wenn man in einem
        // langen Brett eine Aussparung in der Mitte haben will, kriegt man statt dessen ein sich stark zur Mitte hin verjüngendes Brett..

        [[links,this.linksNeedsGriff],[rechts,this.rechtsNeedsGriff]].forEach(([side,needsGriff])=>{
            if (!this.isShojigriff && !this.isOhneGriff && needsGriff) {
                const center = side.box.grid(0.5,0,5,1);
                center.y = this.getGriffCenterY();
                side.griffBox = new THREE.Box3().setFromCenterAndSize(center,this.griffMuldeSize);
                side.griffBox.min.z=side.box.max.z-this.griffMuldeSize.z;
                side.griffBox.max.z=side.box.max.z+1;

                const schatten = lazyGriffschatten.next();
                schatten.fixedMaterialKey = 'schatten';
                schatten.box.copy(side.griffBox);
                schatten.box.max.z = schatten.box.min.z+0.1;

                // BSP Operationen auf Geometrieebene sind hier einfacher als auf Mesh-Ebene,
                // da die Meshes Lazy erzeugt werden, und die Positionierung im Raum nur stören würde
                if (side.oldBox==null || !side.oldBox.isEqualEpsilon(side.box) || side.oldGriffBox==null || !side.oldGriffBox.isEqualEpsilon(side.griffBox)) {
                    side.oldBox=side.oldBox||new THREE.Box3();
                    side.oldBox.copy(side.box);
                    side.oldGriffBox = side.oldGriffBox||new THREE.Box3();
                    side.oldGriffBox.copy(side.griffBox);
                    const basis = side.getBasis();
                    const sourceGeometry = this.boxToGeometry(side.box,basis);
                    const bevelDistance = 0.1;
                    const sideBsp = this.geometryToBsp(sourceGeometry);
                    const griffGeometry = this.bevelConvexUvBufferGeometry(this.boxToGeometry(side.griffBox,basis),bevelDistance,1);
                    const griffBsp = this.geometryToBsp(griffGeometry);
                    // const griffBsp = this.geometryToBsp(this.boxToGeometry(side.griffBox,basis));
                    const resultBsp = sideBsp.sub(griffBsp);
                    side.geometry = resultBsp.toGeometry();

                    griffGeometry.computeBoundingSphere();

                    side.edgesGeometry = new THREE.EdgesGeometry(sourceGeometry);
                }


            } else {
                side.geometry = null;
                side.edgesGeometry = null;
                side.oldBox = null;
                side.oldGriffBox = null;
            }
        });

        this.compileLazyBretter(lazyGriffschatten);
    }

    boxToGeometry(box,basis) {
        const vertices = [
            v3(box.min.x,box.min.y,box.min.z),
            v3(box.min.x,box.min.y,box.max.z),
            v3(box.min.x,box.max.y,box.min.z),
            v3(box.min.x,box.max.y,box.max.z),
            v3(box.max.x,box.min.y,box.min.z),
            v3(box.max.x,box.min.y,box.max.z),
            v3(box.max.x,box.max.y,box.min.z),
            v3(box.max.x,box.max.y,box.max.z)
        ];
        return new ConvexUvBufferGeometry(vertices,basis);
    }

    maybeToGeometry(geometry) {
        if (geometry instanceof THREE.BufferGeometry) {
            const g = new THREE.Geometry().fromBufferGeometry(geometry);
            // g.mergeVertices();
            return g;
        }
        return geometry;
    }

    bevelConvexUvBufferGeometry(convexUvBufferGeometry,bevelDistance=0.01, n=2) {
        for (let i=0;i<n;i++) {
            convexUvBufferGeometry = this.bevelConvexUvBufferGeometryIntern(convexUvBufferGeometry,bevelDistance/n);
        }
        return convexUvBufferGeometry;
    }

    bevelConvexUvBufferGeometryIntern(convexUvBufferGeometry,bevelDistance=0.01) {
        const geometry = this.maybeToGeometry(convexUvBufferGeometry);
        const vertices = [];
        const _0 = v3();
        const _1 = v3();
        const addBeveledPoint = (a,b,c,n) => {
            _0.subVectors(b,a).normalize().multiplyScalar(bevelDistance);
            _1.subVectors(c,a).normalize().multiplyScalar(bevelDistance);
            vertices.push(a.clone().add(_0).add(_1));
        };
        geometry.faces.forEach(f=>{
            const a = geometry.vertices[f.a];
            const b = geometry.vertices[f.b];
            const c = geometry.vertices[f.c];
            addBeveledPoint(a,b,c,f.normal);
            addBeveledPoint(b,c,a,f.normal);
            addBeveledPoint(c,a,b,f.normal);
        });
        return new ConvexUvBufferGeometry(vertices,convexUvBufferGeometry.basis);
    }

    geometryToBsp(geometry) {
        geometry = this.maybeToGeometry(geometry);
        return new ThreeBSP(geometry, new THREE.Matrix4());
    }

    compileLazyBretter(lazyBretter) {
        lazyBretter.array.forEach(r=>{
            r.boxToVertices();
            r.compile();
        });
        lazyBretter.cleanup();
    }

    getFeldAnzahl() {
        const anzahlSprossenX = this.shoji_sprossen_tuerbreite_anzahl_schritte.findIndex(x=> x>this.size.x );
        const feldBreite = this.size.x / (anzahlSprossenX+1);
        let feldHoehe = feldBreite / this.papierSeitenverhaeltnis;
        feldHoehe = Maths.cutBetween(this.papierHoeheMin,feldHoehe,this.papierHoeheMax);
        let anzahlSprossenY = Math.floor(this.size.y / feldHoehe) - 1;
        return v3(anzahlSprossenX, anzahlSprossenY, 1);
    }

    getSprossenAufteilung() {
        const box = this.getInnerRahmenBox();
        const size = box.getSize();
        const feldAnzahl = this.getFeldAnzahl();
        const sprossenAnzahl = feldAnzahl.clone().sub(v3(1,1, 1));
        const feldSize = size.clone().sub(sprossenAnzahl.clone().multiply(this.sprossenMasse)).divide(feldAnzahl);
        feldSize.z = this.schoji_papier_tiefe;
        return {feldSize,sprossenAnzahl,feldAnzahl,size,box};
    }

    getGriffCenterY() {
        if (this.sprossen.length>2) {
            const sprosseNaheWunschhoehe = _.minBy(this.sprossen,sprosse=>Math.abs(sprosse.box.getCenter().y-this.griffMasse.schoji_griff_wunsch_hoehe));
            return sprosseNaheWunschhoehe.box.getCenter().y;
        }
        return this.griffMasse.schoji_griff_wunsch_hoehe;
    }

    getInnerRahmenBox() {
        const box = this.box.clone();
        box.min.x += this.rahmenMasse.xl;
        box.max.x -= this.rahmenMasse.xr;
        box.min.y += this.rahmenMasse.y;
        box.max.y -= this.rahmenMasse.y;
        return box;
    }

    compileSprossenUndPapier() {
        const lazySprossen = APP.model.lazyPool(this, 'sprossen', ()=>new Brett(this, 'Shojituerrahmen'));
        const lazyPapier = APP.model.lazyPool(this, 'papier', ()=>new Brett(this, 'Shojituerpapier'));

        const {feldSize,sprossenAnzahl,feldAnzahl,size,box} = this.getSprossenAufteilung();

        const minPos = box.min.clone();

        for (let xi=0;xi<feldAnzahl.x;xi++) {
            minPos.y = box.min.y;

            if (xi>0) {
                const sprosse = lazySprossen.next();
                sprosse.mainAxes = 'yx';
                sprosse.box.min.copy(minPos);
                sprosse.box.max.copy(sprosse.box.min).add(this.sprossenMasse);
                sprosse.box.max.y = box.max.y;
                minPos.x = sprosse.box.max.x;
            }

            for (let yi=0;yi<feldAnzahl.y;yi++) {

                if (yi>0) {
                    const sprosse = lazySprossen.next();
                    sprosse.mainAxes = 'xy';
                    sprosse.box.min.copy(minPos);
                    sprosse.box.max.copy(sprosse.box.min).add(this.sprossenMasse);
                    // sprosse.box.max.x = box.max.x;
                    sprosse.box.max.x = minPos.x + feldSize.x;
                    minPos.y = sprosse.box.max.y;
                }

                const papier = lazyPapier.next();
                papier.mainAxes = 'xy';
                papier.box.min.copy(minPos);
                papier.box.max.copy(papier.box.min).add(feldSize);
                minPos.y = papier.box.max.y;

            }

            minPos.x += feldSize.x;

        }

        lazySprossen.array.forEach(sprosse=>this.griffBoxen.forEach(gb=>{
            if (gb.intersectsBox(sprosse.box)) {
                if (sprosse.box.min.x<gb.min.x) {
                    sprosse.box.max.x=gb.min.x;
                } else {
                    sprosse.box.min.x=gb.max.x;
                }
            }
        }));

        lazySprossen.array = lazySprossen.array.filter(s=>!s.box.isEmpty());

        this.compileLazyBretter(lazyPapier);
        this.compileLazyBretter(lazySprossen);
    }

    compileShojiGriff() {
        this.griffBoxen = [];

        const lazyGriffRahmen = APP.model.lazyPool(this, 'griffRahmen', ()=>new Brett(this, 'Shojigriffrahmen'));
        const lazyGriffPapier = APP.model.lazyPool(this, 'griffPapier', ()=>new Brett(this, 'Shojigriffpapier'));

        if (this.isShojigriff) {
            const box = this.getInnerRahmenBox();

            const center = box.getCenter();
            center.y = this.getGriffCenterY();
            const papierSize = v3(this.griffMasse.x-this.griffMasse.s,this.griffMasse.y-this.griffMasse.s*2,this.schoji_papier_tiefe*2);

            if (this.linksNeedsGriff) {
                const papier = lazyGriffPapier.next();
                papier.mainAxes = 'xy';
                papier.box.copy(box);
                papier.box.max.x = papier.box.min.x + papierSize.x;
                papier.box.min.y = center.y-papierSize.y/2;
                papier.box.max.y = center.y+papierSize.y/2;
                papier.box.max.z = box.min.z + papierSize.z;

                const outerBox = papier.box.clone();
                outerBox.max.x += this.griffMasse.s;
                outerBox.min.y -= this.griffMasse.s;
                outerBox.max.y += this.griffMasse.s;
                outerBox.max.z = outerBox.min.z + this.rahmenMasse.z;

                const sprosseOben = lazyGriffRahmen.next();
                sprosseOben.mainAxes = 'xy';
                sprosseOben.box.copy(outerBox);
                sprosseOben.box.min.y = papier.box.max.y;

                const sprosseRechts = lazyGriffRahmen.next();
                sprosseRechts.mainAxes = 'yx';
                sprosseRechts.box.copy(outerBox);
                sprosseRechts.box.min.y = papier.box.min.y;
                sprosseRechts.box.max.y = papier.box.max.y;
                sprosseRechts.box.min.x = papier.box.max.x;

                const sprosseUnten = lazyGriffRahmen.next();
                sprosseUnten.mainAxes = 'xy';
                sprosseUnten.box.copy(outerBox);
                sprosseUnten.box.max.y = papier.box.min.y;

                this.griffBoxen.push(outerBox);
            }

            if (this.rechtsNeedsGriff) {
                const papier = lazyGriffPapier.next();
                papier.mainAxes = 'xy';
                papier.box.copy(box);
                papier.box.min.x = papier.box.max.x - papierSize.x;
                papier.box.min.y = center.y-papierSize.y/2;
                papier.box.max.y = center.y+papierSize.y/2;
                papier.box.max.z = box.min.z + papierSize.z;

                const outerBox = papier.box.clone();
                outerBox.min.x -= this.griffMasse.s;
                outerBox.min.y -= this.griffMasse.s;
                outerBox.max.y += this.griffMasse.s;
                outerBox.max.z = outerBox.min.z + this.rahmenMasse.z;

                const sprosseOben = lazyGriffRahmen.next();
                sprosseOben.mainAxes = 'xy';
                sprosseOben.box.copy(outerBox);
                sprosseOben.box.min.y = papier.box.max.y;

                const sprosseRechts = lazyGriffRahmen.next();
                sprosseRechts.mainAxes = 'yx';
                sprosseRechts.box.copy(outerBox);
                sprosseRechts.box.min.y = papier.box.min.y;
                sprosseRechts.box.max.y = papier.box.max.y;
                sprosseRechts.box.max.x = papier.box.min.x;

                const sprosseUnten = lazyGriffRahmen.next();
                sprosseUnten.mainAxes = 'xy';
                sprosseUnten.box.copy(outerBox);
                sprosseUnten.box.max.y = papier.box.min.y;

                this.griffBoxen.push(outerBox);
            }

        }

        this.compileLazyBretter(lazyGriffRahmen);
        this.compileLazyBretter(lazyGriffPapier);
    }

    iterate(iterator) {
        iterator(this);
        this.rahmen.forEach(x=>x.iterate(iterator));
        this.sprossen.forEach(x=>x.iterate(iterator));
        this.papier.forEach(x=>x.iterate(iterator));
        this.griffRahmen.forEach(x=>x.iterate(iterator));
        this.griffPapier.forEach(x=>x.iterate(iterator));
    }

    get arrangement() {
        return this.schrank.arrangement;
    }

}

